import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * Header cart: `item_count` from `/cart.js`. Count renders as `(n)` for desktop text;
 * mobile icon layouts show a bare number via `data-count`. Bag SVG fills while the
 * cart drawer is open.
 *
 * @param {unknown} cart
 * @returns {number | null}
 */
function parseItemCountFromCartLike(cart) {
  const raw = /** @type {{ item_count?: unknown }} */ (cart)?.item_count;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/** Coalesce parallel `/cart.js` reads when several `<cart-icon>` instances handle the same event */
let sharedCartItemCountPromise = /** @type {Promise<number | null> | null} */ (null);

async function fetchShopifyCartItemCount() {
  if (sharedCartItemCountPromise) return sharedCartItemCountPromise;

  sharedCartItemCountPromise = (async () => {
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store'
      });
      const cart = await response.json();
      return parseItemCountFromCartLike(cart);
    } catch {
      return null;
    } finally {
      sharedCartItemCountPromise = null;
    }
  })();

  return sharedCartItemCountPromise;
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble
 * @property {HTMLElement} cartBubbleText
 * @property {HTMLElement} cartBubbleCount
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** Last `item_count` we rendered (parses from `(n)` / `(99+)` / bare `n` in the DOM). */
  #lastRenderedTotal = /** @type {number | null} */ (null);

  /** @type {number} */
  get currentCartCount() {
    return this.#lastRenderedTotal ?? this.#parseVisibleBubbleNumber();
  }

  set currentCartCount(value) {
    const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
    const bare = n === 0 ? '' : n < 100 ? String(n) : '99+';
    this.refs.cartBubbleCount.textContent = bare === '' ? '' : `(${bare})`;
    if (bare) {
      this.refs.cartBubbleCount.dataset.count = bare;
    } else {
      delete this.refs.cartBubbleCount.dataset.count;
    }
  }

  /** @returns {number} */
  #parseVisibleBubbleNumber() {
    const fromData = this.refs.cartBubbleCount.dataset.count?.trim();
    if (fromData) {
      if (fromData === '99+') return 100;
      const n = parseInt(fromData, 10);
      return Number.isFinite(n) ? n : 0;
    }

    const raw = this.refs.cartBubbleCount.textContent?.trim() ?? '';
    if (raw === '') return 0;
    const wrapped = raw.match(/^\((.+)\)$/);
    const value = wrapped ? wrapped[1] : raw;
    if (value === '99+') return 100;
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : 0;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.#lastRenderedTotal = this.#parseVisibleBubbleNumber();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /** @param {PageTransitionEvent} event */
  onPageShow = (event) => {
    if (event.persisted) {
      void this.#syncFromShopify();
    }
  };

  onCartUpdate = async () => {
    const count = await fetchShopifyCartItemCount();
    if (count == null) return;

    const prev = this.#lastRenderedTotal ?? this.#parseVisibleBubbleNumber();
    const animate = count > prev;

    await this.renderCartBubble(count, animate);
  };

  #syncFromShopify = async () => {
    const count = await fetchShopifyCartItemCount();
    if (count == null) return;
    await this.renderCartBubble(count, false);
  };

  /**
   * @param {number} count
   * @param {boolean} [animate=true]
   */
  renderCartBubble = async (count, animate = true) => {
    this.#lastRenderedTotal = count;

    this.refs.cartBubbleCount.classList.toggle('hidden', count === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', count === 0);

    this.currentCartCount = count;
    this.#syncAccessibleCount(count);

    this.toggleAttribute('data-has-cart', count > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now()
      })
    );

    if (!animate || count === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /** @param {number} itemCount */
  #syncAccessibleCount(itemCount) {
    const accessibleCount = this.refs.cartBubbleText.querySelector('.visually-hidden');
    if (!accessibleCount) return;

    const label = accessibleCount.textContent?.split(':')[0]?.trim() || 'Cart count';
    accessibleCount.textContent = `${label}: ${itemCount}`;
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
